/**
 * Integrations Service
 * 
 * Handles API calls for managing integrations
 */

import {apiClient} from './api-client';
import type {Integration, IntegrationStatus} from '../types/integration.types';

export interface SaveIntegrationData {
  id?: number;
  form_id?: number | null;
  provider: string;
  name?: string;
  config: any;
  status?: IntegrationStatus;
}

class IntegrationsService {
  /**
   * Get integrations list
   */
  async getIntegrations(formId?: number): Promise<{ items: Integration[]; total: number; page: number; perPage: number }> {
    try {
      const params: any = {};
      if (formId) {
        params.form_id = formId;
      }

      return await apiClient.callProxy('integrations', 'getIntegrations', params);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      throw error;
    }
  }

  /**
   * Get integration statistics (efficient backend aggregation)
   */
  async getStats(): Promise<any> {
    try {
      return await apiClient.callProxy('integrations', 'getStats', {});
    } catch (error) {
      console.error('Failed to fetch integration stats:', error);
      throw error;
    }
  }

  /**
   * Get single integration
   */
  async getIntegration(id: number): Promise<Integration> {
    try {
      const response = await apiClient.callProxy('integrations', 'getIntegration', { id });

      // Handle both wrapped and direct responses
      if (response && response.data !== undefined) {
        return response.data;
      }

      // If response is the data itself
      return response;
    } catch (error) {
      console.error('Failed to fetch integration:', error);
      throw error;
    }
  }

  /**
   * Save integration (create or update)
   */
  async saveIntegration(data: SaveIntegrationData): Promise<Integration> {
    try {
      const response = await apiClient.callProxy('integrations', 'saveIntegration', data);

      // If response is the data directly (number), return it
      if (typeof response === 'number') {
        return response as any;
      }

      // If response has data property, return it
      if (response && response.data !== undefined) {
        return response.data;
      }

      // Otherwise return the response itself
      return response;
    } catch (error) {
      console.error('Failed to save integration:', error);
      throw error;
    }
  }

  /**
   * Delete integration
   */
  async deleteIntegration(id: number): Promise<void> {
    try {
      await apiClient.callProxy('integrations', 'deleteIntegration', { id });
    } catch (error) {
      console.error('Failed to delete integration:', error);
      throw error;
    }
  }

  /**
   * Test integration connection
   */
  async testIntegration(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.callProxy('integrations', 'testIntegration', { id });
      return response;
    } catch (error) {
      console.error('Failed to test integration:', error);
      return { success: false, message: 'Failed to test integration' };
    }
  }

  /**
   * Get available providers (enabled for current user)
   */
  async getAvailableProviders(): Promise<any[]> {
    try {
      const response = await apiClient.callProxy('integrations', 'getProviders', {});

      // Handle object format (keyed by provider_id)
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        return Object.values(response);
      }

      // Check if response has providers property (new format)
      if (response && response.providers && Array.isArray(response.providers)) {
        return response.providers;
      }

      // Otherwise return as array if it is one
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Failed to fetch available providers:', error);
      throw error;
    }
  }

  /**
   * Get all providers with user groups (admin only)
   */
  async getAllProviders(): Promise<{ providers: any[]; userGroups: any[] }> {
    try {
      const response = await apiClient.callProxy('integrations', 'getAllProviders', {});

      // Handle both old format (array) and new format (object with providers and userGroups)
      if (Array.isArray(response)) {
        // Old format - return providers with empty userGroups
        return { providers: response, userGroups: [] };
      } else if (response && response.providers) {
        // New format - return both
        return {
          providers: Array.isArray(response.providers) ? response.providers : [],
          userGroups: Array.isArray(response.userGroups) ? response.userGroups : []
        };
      }

      // Fallback
      return { providers: [], userGroups: [] };
    } catch (error) {
      console.error('Failed to fetch all providers:', error);
      throw error;
    }
  }

  /**
   * Enable provider (admin only)
   */
  async enableProvider(providerId: string, settings: any = {}): Promise<void> {
    try {
      await apiClient.callProxy('integrations', 'enableProvider', {
        providerId,
        settings
      });
    } catch (error) {
      console.error('Failed to enable provider:', error);
      throw error;
    }
  }

  /**
   * Disable provider (admin only)
   */
  async disableProvider(providerId: string): Promise<void> {
    try {
      await apiClient.callProxy('integrations', 'disableProvider', { providerId });
    } catch (error) {
      console.error('Failed to disable provider:', error);
      throw error;
    }
  }

  /**
   * Update provider settings (admin only)
   */
  async updateProviderSettings(providerId: string, settings: any): Promise<void> {
    try {
      await apiClient.callProxy('integrations', 'updateProviderSettings', {
        providerId,
        ...settings
      });
    } catch (error) {
      console.error('Failed to update provider settings:', error);
      throw error;
    }
  }

  /**
   * Get integrations for a specific form
   */
  async getFormIntegrations(formId: number): Promise<Integration[]> {
    try {
      const response = await apiClient.callProxy('integrations', 'getIntegrations', {
        form_id: formId
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch form integrations:', error);
      throw error;
    }
  }
}

export const integrationsService = new IntegrationsService();

